// Activity Context for Provider Dashboard Activity Logging
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import FirebaseActivityService from '../services/firebase/FirebaseActivityService';
import {
  ActivityLog,
  ActivityFilters,
  HopeConversationSummary,
  DailyActivitySummary,
  LogActivityRequest,
  UnsubscribeFunction
} from '../types/activity';

interface ActivityContextType {
  // State
  activities: ActivityLog[];
  selectedActivity: ActivityLog | null;
  selectedConversation: HopeConversationSummary | null;
  hopeConversations: HopeConversationSummary[];
  dailySummary: DailyActivitySummary | null;
  unreadCount: number;
  filters: ActivityFilters;
  loading: boolean;
  error: string | null;

  // Actions
  setFilters: (filters: ActivityFilters) => void;
  selectActivity: (activity: ActivityLog | null) => void;
  selectConversation: (conversationId: string | null) => void;
  markAsRead: (activityId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  logActivity: (request: Omit<LogActivityRequest, 'providerId'>) => Promise<string>;
  refreshActivities: () => void;
  loadMoreActivities: () => Promise<void>;
}

const ActivityContext = createContext<ActivityContextType | undefined>(undefined);

interface ActivityProviderProps {
  children: ReactNode;
}

export const ActivityProvider: React.FC<ActivityProviderProps> = ({ children }) => {
  const { user, isProvider, userProfile, roles } = useAuth();
  const providerId = userProfile?.uid || user?.uid || '';
  // System admins and clinical directors can also view the activity feed
  const canViewActivity = isProvider || roles.includes('system_admin') || roles.includes('clinical_director');

  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<ActivityLog | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<HopeConversationSummary | null>(null);
  const [hopeConversations, setHopeConversations] = useState<HopeConversationSummary[]>([]);
  const [dailySummary, setDailySummary] = useState<DailyActivitySummary | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filters, setFiltersState] = useState<ActivityFilters>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activityService = FirebaseActivityService.getInstance();

  // Subscribe to activities when provider/admin is authenticated
  useEffect(() => {
    if (!providerId || !canViewActivity) {
      setActivities([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = activityService.subscribeToActivities(
      providerId,
      (newActivities) => {
        setActivities(newActivities);
        setLoading(false);
        setError(null);
      },
      filters
    );

    return () => unsubscribe();
  }, [providerId, canViewActivity, filters]);

  // Subscribe to unread count
  useEffect(() => {
    if (!providerId || !isProvider) {
      setUnreadCount(0);
      return;
    }

    const unsubscribe = activityService.subscribeToUnreadCount(
      providerId,
      setUnreadCount
    );

    return () => unsubscribe();
  }, [providerId, isProvider]);

  // Subscribe to Hope conversations
  useEffect(() => {
    if (!providerId || !isProvider) {
      setHopeConversations([]);
      return;
    }

    const unsubscribe = activityService.subscribeToHopeConversations(
      providerId,
      setHopeConversations
    );

    return () => unsubscribe();
  }, [providerId, isProvider]);

  // Load daily summary
  useEffect(() => {
    if (!providerId || !isProvider) {
      setDailySummary(null);
      return;
    }

    const loadDailySummary = async () => {
      const today = new Date().toISOString().split('T')[0];
      const summary = await activityService.getDailySummary(providerId, today);
      setDailySummary(summary);
    };

    loadDailySummary();
  }, [providerId, isProvider, activities]); // Reload when activities change

  // Set filters
  const setFilters = useCallback((newFilters: ActivityFilters) => {
    setFiltersState(newFilters);
  }, []);

  // Select an activity
  const selectActivity = useCallback((activity: ActivityLog | null) => {
    setSelectedActivity(activity);

    // If selecting an activity with a Hope conversation, load its details
    if (activity?.metadata?.conversationId) {
      selectConversation(activity.metadata.conversationId);
    } else {
      setSelectedConversation(null);
    }
  }, []);

  // Select a Hope conversation
  const selectConversation = useCallback(async (conversationId: string | null) => {
    if (!conversationId) {
      setSelectedConversation(null);
      return;
    }

    try {
      const conversation = await activityService.getHopeConversation(conversationId);
      setSelectedConversation(conversation);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setSelectedConversation(null);
    }
  }, []);

  // Mark activity as read
  const markAsRead = useCallback(async (activityId: string) => {
    try {
      await activityService.markActivityAsRead(activityId);
    } catch (err) {
      console.error('Error marking activity as read:', err);
      setError('Failed to mark activity as read');
    }
  }, []);

  // Mark all activities as read
  const markAllAsRead = useCallback(async () => {
    try {
      const unreadIds = activities
        .filter(a => !a.read)
        .map(a => a.id);

      if (unreadIds.length > 0) {
        await activityService.markActivitiesAsRead(unreadIds);
      }
    } catch (err) {
      console.error('Error marking all activities as read:', err);
      setError('Failed to mark activities as read');
    }
  }, [activities]);

  // Log a new activity
  const logActivity = useCallback(async (
    request: Omit<LogActivityRequest, 'providerId'>
  ): Promise<string> => {
    if (!providerId) {
      throw new Error('No provider ID available');
    }

    try {
      const activityId = await activityService.logActivity({
        ...request,
        providerId
      });
      return activityId;
    } catch (err) {
      console.error('Error logging activity:', err);
      setError('Failed to log activity');
      throw err;
    }
  }, [providerId]);

  // Refresh activities (trigger re-subscription)
  const refreshActivities = useCallback(() => {
    setLoading(true);
    // The subscription will automatically update
    setTimeout(() => setLoading(false), 500);
  }, []);

  // Load more activities (pagination)
  const loadMoreActivities = useCallback(async () => {
    // This would be implemented with cursor-based pagination
    // For now, just refresh
    refreshActivities();
  }, [refreshActivities]);

  const value: ActivityContextType = {
    activities,
    selectedActivity,
    selectedConversation,
    hopeConversations,
    dailySummary,
    unreadCount,
    filters,
    loading,
    error,
    setFilters,
    selectActivity,
    selectConversation,
    markAsRead,
    markAllAsRead,
    logActivity,
    refreshActivities,
    loadMoreActivities
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

export const useActivity = (): ActivityContextType => {
  const context = useContext(ActivityContext);
  if (context === undefined) {
    throw new Error('useActivity must be used within an ActivityProvider');
  }
  return context;
};

export default ActivityContext;
